import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import type { HistoryState } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import { postPipelineCandidatesOptions } from 'client/@tanstack/react-query.gen';
import type {
  DeclinedStatusFilter,
  IntroductionStatusFilter,
  PipelineCandidatesFilters,
  PipelineCandidateSortColumn,
  PipelineCandidateSearchFilterSortResponse,
  RecommendationStatusFilter,
} from 'client/types.gen';
import {
  useTableFilters,
  useTablePagination,
  useTableSearch,
  useTableSorting,
} from 'components/UI/Tailwind/Table';

import type { FiltersDialogKey } from './search';

type Filters = Required<NonNullableProperties<PipelineCandidatesFilters>>;
type Sort = PipelineCandidateSortColumn;

// Maps each dialog tab to the aggregate_fields it needs from the API.
const TAB_AGGREGATE_FIELDS: Record<FiltersDialogKey, string[]> = {
  advisor_id: ['advisor_id'],
  connection_history: [
    'recommendation_status',
    'introduction_status',
    'declined_status',
  ],
  function_expertise: ['function_expertise'],
  industry_expertise: ['industry_expertise'],
  is_accepting_intros: ['is_accepting_intros'],
  operating_highlights: ['operating_highlights'],
  recsys_suggested: ['recsys_suggested'],
  tier: ['tier'],
  type: ['type'],
  pathway: ['pathway'],
  underrepresented: ['underrepresented'],
  warm: ['warm'],
};

interface NewRecommendationsContextType {
  selectedCandidates: Record<string, boolean>;
  candidateObjects: Record<string, PipelineCandidateSearchFilterSortResponse>;
  addCandidate: (
    uuid: string,
    candidate: PipelineCandidateSearchFilterSortResponse,
  ) => void;
  removeCandidate: (uuid: string) => void;
  clearSelectedCandidates: () => void;
  getSelectedCandidateUuids: () => string[];
  getSelectedCandidates: () => PipelineCandidateSearchFilterSortResponse[];
  setSelectedCandidates: (candidates: Record<string, boolean>) => void;
  queryCandidatesFilterOptions: ReturnType<
    typeof postPipelineCandidatesOptions
  >;
  queryCandidatesFilterStagedAggregationOptions: ReturnType<
    typeof postPipelineCandidatesOptions
  >;
  queryCandidatesFilterStagedTotals: ReturnType<
    typeof postPipelineCandidatesOptions
  >;
  pagination: ReturnType<typeof useTablePagination>[0];
  setPagination: ReturnType<typeof useTablePagination>[1];
  filterState: ReturnType<
    typeof useTableFilters<Omit<Filters, 'pipeline_status'>>
  >;
  searchState: ReturnType<typeof useTableSearch>;
  sortingState: ReturnType<typeof useTableSorting<Sort>>;
  pipelineId: string;
  teamId: string;
  /** Currently open filter dialog tab, synced to the surface's URL search. */
  filtersDialog?: FiltersDialogKey;
  /** Open/close a filter dialog tab via the surface's URL search. */
  setFiltersDialog: (key?: FiltersDialogKey) => void;
  /** Navigate back to the surface's pipeline detail page. */
  onExit: () => void;
  /** Accumulated set of aggregate_fields seen since the dialog was opened. */
  seenAggregateFields: string[];
  /**
   * Picks (candidates already on the pipeline) the advisor switched off as signals for
   * the Suggested list this session (BAN-6235). Not in the URL: a reload starts over.
   */
  excludedPicks: string[];
  /** Replace the switched-off picks; the list re-ranks from page one. */
  setExcludedPicks: (excludedPicks: string[]) => void;
}

const NewRecommendationsContext = createContext<
  NewRecommendationsContextType | undefined
>(undefined);

interface NewRecommendationsProviderProps {
  children: ReactNode;
  pipelineId: string;
  initialState: HistoryState;
  teamId: string;
  filtersDialog?: FiltersDialogKey;
  setFiltersDialog: (key?: FiltersDialogKey) => void;
  onExit: () => void;
}

export function NewRecommendationsProvider({
  children,
  pipelineId,
  initialState,
  teamId,
  filtersDialog,
  setFiltersDialog,
  onExit,
}: NewRecommendationsProviderProps) {
  // The state object that react-table expects for row selection.
  const [selectedCandidates, setSelectedCandidates] = useState<
    Record<string, boolean>
  >(initialState.selectedCandidates || {});

  const [candidateObjects, setCandidateObjects] = useState<
    Record<string, PipelineCandidateSearchFilterSortResponse>
  >(initialState.candidateObjects || {});

  const addCandidate = useCallback(
    (uuid: string, candidate: PipelineCandidateSearchFilterSortResponse) => {
      setSelectedCandidates((prev) => ({ ...prev, [uuid]: true }));
      setCandidateObjects((prev) => ({ ...prev, [uuid]: candidate }));
    },
    [],
  );

  const removeCandidate = useCallback(
    (uuid: string) => {
      setSelectedCandidates((prev) => {
        const newState = { ...prev };
        delete newState[uuid];
        return newState;
      });
      setCandidateObjects((prev) => {
        const newState = { ...prev };
        delete newState[uuid];
        return newState;
      });
    },
    [setSelectedCandidates],
  );

  const clearSelectedCandidates = useCallback(() => {
    setSelectedCandidates({});
    setCandidateObjects({});
  }, []);

  const getSelectedCandidateUuids = useCallback(() => {
    return Object.keys(selectedCandidates);
  }, [selectedCandidates]);

  const getSelectedCandidates = useCallback(() => {
    return Object.values(candidateObjects).filter(
      (candidate) => selectedCandidates[candidate.uuid],
    );
  }, [candidateObjects, selectedCandidates]);

  // Accumulates aggregate_fields as tabs are visited; resets when dialog closes.
  const [seenAggregateFields, setSeenAggregateFields] = useState<string[]>([]);

  // When filtersDialog changes: if a tab is being opened/switched, grow the set.
  // If it closes (undefined), reset so the next open starts fresh.
  useMemo(() => {
    if (!filtersDialog) {
      setSeenAggregateFields([]);
    } else {
      const incoming = TAB_AGGREGATE_FIELDS[filtersDialog] ?? [];
      setSeenAggregateFields((prev) => {
        const next = [...new Set([...prev, ...incoming])];
        return next.length === prev.length ? prev : next;
      });
    }
  }, [filtersDialog]);

  // Table state
  const [pagination, setPagination] = useTablePagination(undefined, {
    useQueryParams: true,
  });

  const filterState = useTableFilters<Omit<Filters, 'pipeline_status'>>(
    {
      advisor_id: [],
      declined_status: [] as DeclinedStatusFilter[],
      function_expertise: [],
      industry_expertise: [],
      introduction_status: [] as IntroductionStatusFilter[],
      is_accepting_intros: [],
      operating_highlights: [],
      pathway: [],
      recommendation_status: [] as RecommendationStatusFilter[],
      // Default ON (BAN-5920). Off is an explicit [false], never [], so it
      // survives a URL round-trip (an empty array vanishes from the URL and
      // would resurrect the default).
      recsys_suggested: [true],
      tier: [],
      type: [],
      underrepresented: [],
      warm: [],
    },
    {
      setPagination,
      useQueryParams: true,
    },
  );

  const { state: filters, staged } = filterState;

  // No initial sort column: the hook falls back to its initialState whenever
  // the URL has no `sort`, which would resurrect `created` right after
  // `onSearch` clears it and override the API's relevance order for phrase
  // searches (BAN-5921). The default lives in `sort_rules` below instead, so
  // an empty sorting state can mean "let the API rank by relevance".
  const sortingState = useTableSorting<Sort>(
    {
      order: 'desc',
    },
    {
      useQueryParams: true,
    },
  );

  const [sorting, setSorting] = sortingState;

  const searchState = useTableSearch({
    onSearch: () => {
      setSorting([{ desc: false, id: '' }]);
    },
    setPagination,
  });

  const [excludedPicks, setExcludedPicksState] = useState<string[]>([]);
  const setExcludedPicks = useCallback(
    (next: string[]) => {
      setExcludedPicksState(next);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setPagination],
  );

  // Queries only the committed filters.
  // Results are sorted, filtered, and paginated according to the URL query params.
  const queryCandidatesFilterOptions = useMemo(() => {
    // Explicit user sort always wins. Otherwise empty rules defer to the API's
    // own default: RecSys run rank while the Suggested filter is on, relevance
    // order while searching. Plain browsing falls back to newest-first.
    const suggestedActive = filters.recsys_suggested[0] === true;
    const defaultSortRules =
      searchState.debouncedPhrase || suggestedActive
        ? []
        : [{ column: 'created' as const, direction: 'desc' as const }];
    const sortRules = sorting[0]?.id
      ? [
          {
            column: sorting[0].id,
            direction: sorting[0]?.desc ? ('desc' as const) : ('asc' as const),
          },
        ]
      : defaultSortRules;

    return {
      enabled: Boolean(pipelineId && teamId),
      placeholderData: keepPreviousData,
      ...postPipelineCandidatesOptions({
        path: { pipeline_uuid: pipelineId, team_uuid: teamId },
        body: {
          filter_data: {
            ...filters,
            // Special case: API only considers the first element of a boolean array filter.
            // If someone selects both, send empty array instead to get desired response.
            warm: filters.warm.length > 1 ? [] : filters.warm,
            underrepresented:
              filters.underrepresented.length > 1
                ? []
                : filters.underrepresented,
            is_accepting_intros:
              filters.is_accepting_intros.length > 1
                ? []
                : filters.is_accepting_intros,
            recsys_suggested:
              filters.recsys_suggested.length > 1
                ? []
                : filters.recsys_suggested,
          },
          page: pagination.pageIndex + 1,
          phrase: searchState.debouncedPhrase,
          page_size: pagination.pageSize,
          sort_rules: sortRules,
          suggestion_excluded_picks:
            suggestedActive && excludedPicks.length ? excludedPicks : undefined,
        },
      }),
    };
  }, [
    pipelineId,
    teamId,
    filters,
    pagination,
    searchState.debouncedPhrase,
    sorting,
    excludedPicks,
  ]);

  // Queries candidates on the staged filters,
  // omitting the currently viewed filter category.
  // This provides the correct aggregation totals to display in the filter dialog.
  // Not affected by the URL query params (e.g. search, pagination, sorting uses set defaults).
  const queryCandidatesFilterStagedAggregationOptions = useMemo(() => {
    // Zero out the fields for the currently-viewed tab so counts reflect the
    // candidate pool before that tab's filter is applied.
    const currentTabFields = filtersDialog
      ? (TAB_AGGREGATE_FIELDS[filtersDialog] ?? [filtersDialog])
      : [];
    const zeroedCurrentTab = Object.fromEntries(
      currentTabFields.map((f) => [f, []]),
    );

    return {
      ...postPipelineCandidatesOptions({
        body: {
          aggregate_fields:
            seenAggregateFields.length > 0 ? seenAggregateFields : undefined,
          include_results: false,
          filter_data: {
            ...staged,
            warm: staged.warm.length > 1 ? [] : staged.warm,
            underrepresented:
              staged.underrepresented.length > 1 ? [] : staged.underrepresented,
            is_accepting_intros:
              staged.is_accepting_intros.length > 1
                ? []
                : staged.is_accepting_intros,
            recsys_suggested:
              staged.recsys_suggested.length > 1 ? [] : staged.recsys_suggested,
            ...zeroedCurrentTab,
          },
          page: 1,
          page_size: 100,
          phrase: searchState.debouncedPhrase,
          sort_rules: [],
        },
        path: {
          pipeline_uuid: pipelineId,
          team_uuid: teamId,
        },
      }),
    };
  }, [
    pipelineId,
    teamId,
    staged,
    searchState.debouncedPhrase,
    filtersDialog,
    seenAggregateFields,
  ]);

  // Queries candidates on all staged filters,
  // including the currently viewed filter category (from the URL query params).
  // This provides the correct totals to display in the filter dialog.
  // Not affected by the URL query params (e.g. search, pagination, sorting uses set defaults).
  const queryCandidatesFilterStagedTotals = useMemo(
    () => ({
      ...postPipelineCandidatesOptions({
        body: {
          include_results: false,
          filter_data: {
            ...staged,
            warm: staged.warm.length > 1 ? [] : staged.warm,
            underrepresented:
              staged.underrepresented.length > 1 ? [] : staged.underrepresented,
            is_accepting_intros:
              staged.is_accepting_intros.length > 1
                ? []
                : staged.is_accepting_intros,
            recsys_suggested:
              staged.recsys_suggested.length > 1 ? [] : staged.recsys_suggested,
          },
          page: 1,
          page_size: 100,
          phrase: searchState.debouncedPhrase,
          sort_rules: [],
        },
        path: {
          pipeline_uuid: pipelineId,
          team_uuid: teamId,
        },
      }),
    }),
    [pipelineId, teamId, staged, searchState.debouncedPhrase],
  );

  return (
    <NewRecommendationsContext.Provider
      value={
        {
          selectedCandidates,
          candidateObjects,
          addCandidate,
          removeCandidate,
          clearSelectedCandidates,
          getSelectedCandidateUuids,
          getSelectedCandidates,
          setSelectedCandidates,
          queryCandidatesFilterOptions,
          queryCandidatesFilterStagedAggregationOptions,
          queryCandidatesFilterStagedTotals,
          pagination,
          setPagination,
          filterState,
          searchState,
          sortingState,
          pipelineId,
          teamId,
          filtersDialog,
          setFiltersDialog,
          onExit,
          seenAggregateFields,
          excludedPicks,
          setExcludedPicks,
        } satisfies NewRecommendationsContextType
      }
    >
      {children}
    </NewRecommendationsContext.Provider>
  );
}

export function useNewRecommendationsContext() {
  const context = useContext(NewRecommendationsContext);
  if (context === undefined) {
    throw new Error(
      'useNewRecommendationsContext must be used within a NewRecommendationsProvider',
    );
  }
  return context;
}
